/**
 * "Shared with you" (D276): Chat and Research conversations a colleague shared live with the project or with
 * this person. Each row says whose it is and who it is shared with; one opens read only, the messages as
 * the owner's workbench uploaded them. Nothing here when nothing is shared.
 */

import { audienceWords } from '@aioe/shared';
import { Comment } from '@gravity-ui/icons';
import { Button, Modal } from '@heroui/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Markdown } from './Markdown.tsx';
import { ErrorNote, ListGroup, ListRow } from './ui.tsx';

interface Open {
  owner: { id: string; name: string };
  id: string;
  title: string;
}

export function SharedWithYou({ projectId }: { projectId: string }) {
  const list = useLoad(() => routes.sharedConversations(projectId), [projectId], 60_000);
  const [open, setOpen] = useState<Open>();
  const rows = list.data?.conversations ?? [];
  if (!rows.length && !list.error) return null;
  return (
    <>
      <ErrorNote error={list.error} />
      {rows.length ? (
        <ListGroup title="Shared with you" count={rows.length} description="Chat and Research conversations colleagues shared. Read only.">
          {rows.map((c) => (
            <ListRow
              key={`${c.owner.id}/${c.id}`}
              icon={<Comment />}
              title={c.data.title || 'Untitled conversation'}
              description={`${c.owner.name} · ${c.data.kind === 'research' ? 'Research' : 'Chat'} · ${audienceWords(c.audience)} · updated ${timeAgo(c.updatedAt)}`}
              onPress={() => setOpen({ owner: c.owner, id: c.id, title: c.data.title })}
            />
          ))}
        </ListGroup>
      ) : null}
      {open ? <Reader projectId={projectId} open={open} onClose={() => setOpen(undefined)} /> : null}
    </>
  );
}

function Reader({ projectId, open, onClose }: { projectId: string; open: Open; onClose: () => void }) {
  const msgs = useLoad(() => routes.sharedConversationMessages(projectId, open.owner.id, open.id), [projectId, open.owner.id, open.id]);
  return (
    <Modal isOpen onOpenChange={(o) => !o && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="lg" scroll="inside">
          <Modal.Dialog aria-label={open.title || 'Shared conversation'}>
            <Modal.Header className="flex-row items-center justify-between gap-3">
              <Modal.Heading className="min-w-0 truncate">{open.title || 'Untitled conversation'}</Modal.Heading>
              <Button size="sm" variant="secondary" onPress={onClose}>
                Close
              </Button>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-3">
              <p className="m-0 text-xs text-muted">{open.owner.name} shared this. Read only.</p>
              <ErrorNote error={msgs.error} />
              {!msgs.data && !msgs.error ? <p className="m-0 text-sm text-muted">Reading the conversation…</p> : null}
              {(msgs.data?.entries ?? []).map((e) => (
                <div key={e.seq} className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted">{e.data.role === 'user' ? (e.data.author?.name ?? open.owner.name) : e.data.role === 'assistant' ? 'The assistant' : 'Note'}</span>
                  <Markdown text={e.data.text} compact />
                </div>
              ))}
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
